import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Trash2Icon, PlusIcon, ChevronDownIcon, ChevronRightIcon, CalendarDaysIcon } from 'lucide-react'
import {
  VisitGroupServiceAssignment,
  getGroupAssignments,
  resolveVisitGroupDisplayDays,
  syncGroupServiceIds,
  groupRecurrenceMatchesDate,
} from '../lib/visitGroupBoardOps'
import { useChecklistTemplates } from '../hooks/useChecklistTemplates'
import { normalizeChecklistItems, sortTemplatesForServiceType } from '../lib/checklistTemplates'
import { fetchCrewsAndEmployees } from '../api/jobWorkflow'
import type { CrewOption, EmployeeOption } from '../api/jobWorkflow'
import { formatEmployeeLabel } from '../lib/formatEmployeeLabel'

export interface ContractVisitGroup {
  id: string
  contract_id: string
  name: string
  recurrence_type: string
  recurrence_days: number[]
  /** Monthly recurrence detail (nth-weekday). Only used when recurrence_type = 'monthly'. */
  recurrence_weekday?: number | null
  recurrence_occurrence?: number | null
  /** Monthly recurrence detail (day-of-month). Only used when recurrence_type = 'monthly'. */
  recurrence_day_of_month?: number | null
  start_date?: string | null
  end_date?: string | null
  scheduled_start_time?: string | null
  scheduled_end_time?: string | null
  billing_driver?: string | null
  dsny_integration?: boolean
  dsny_pickup_days?: string[]
  is_active?: boolean
  /** Checklist seeded into jobs generated from this visit group. */
  checklist_items?: Array<{ label: string; sort_order: number; photo_required?: boolean }>
  /**
   * Per catalogue item overrides keyed by service_item_id.
   * Present key → use this list instead of service_items.default_checklist when seeding jobs.
   */
  checklist_overrides?: Record<
    string,
    Array<{ label: string; sort_order: number; photo_required?: boolean }>
  >
  /** Optional override; null/empty inherits contract/property default. */
  assigned_crew_id?: string | null
  assigned_employee_id?: string | null
  service_ids: string[]
  /** Per-service schedule overrides (piggyback). Falls back to service_ids when absent. */
  service_assignments?: VisitGroupServiceAssignment[]
}

export interface ContractServiceLite {
  id: string
  service_name?: string
  name?: string
  recurrence_days?: number[]
  // Phase 11 seasonal: service catalogue can mark items seasonal so visit
  // groups containing them expose start/end date inputs by default.
  service_item_id?: string
  is_seasonal?: boolean
  season_start_month?: number | null
  season_end_month?: number | null
  /** Optional catalogue work copy for read-only preview under services. */
  work_description?: string | null
  default_checklist?: Array<{ label: string; sort_order: number; photo_required?: boolean }>
}

export interface VisitGroupTemplateOption {
  id: string
  name: string
  description?: string
  recurrence_type?: string
  recurrence_days?: number[]
  scheduled_start_time?: string | null
  scheduled_end_time?: string | null
  dsny_integration?: boolean
  dsny_pickup_days?: string[]
  applicable_service_types?: string[]
}

interface VisitGroupEditorProps {
  visitGroups: ContractVisitGroup[]
  onChange: (groups: ContractVisitGroup[]) => void
  contractServices: ContractServiceLite[]
  contractStartDate?: string | null
  contractEndDate?: string | null
  contractId?: string
  showAddButton?: boolean
  showSaveButton?: boolean
  showPreview?: boolean
  onSave?: () => void
  saving?: boolean
  onDelete?: (groupId: string) => Promise<void> | void
  templates?: VisitGroupTemplateOption[]
  onSaveAsTemplate?: (group: ContractVisitGroup) => Promise<void> | void
  emptyMessage?: string
  title?: string
  subtitle?: string
  /**
   * When true, recurrence days and DSNY pickup days come from the contract's
   * Services & Schedule section and are shown read-only here so days are only
   * ever entered in one place.
   */
  scheduleDriven?: boolean
  crewOptions?: CrewOption[]
  employeeOptions?: EmployeeOption[]
}

const DAY_PILLS = [
  { key: 0, label: 'Sun' },
  { key: 1, label: 'Mon' },
  { key: 2, label: 'Tue' },
  { key: 3, label: 'Wed' },
  { key: 4, label: 'Thu' },
  { key: 5, label: 'Fri' },
  { key: 6, label: 'Sat' },
]

const DSNY_DAY_OPTIONS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/** Human-readable cadence for visit group previews, e.g. "Sun, Tue" or "Monthly on day 15". */
export function describeVisitGroupCadence(
  group: ContractVisitGroup,
  services?: ContractServiceLite[]
): string {
  const rtype = group.recurrence_type || 'weekly'
  if (rtype === 'daily') return 'Daily'
  if (rtype === 'monthly') {
    if (group.recurrence_day_of_month) {
      return `Monthly on day ${group.recurrence_day_of_month}`
    }
    if (group.recurrence_weekday != null && group.recurrence_occurrence != null) {
      const ordinals = ['1st', '2nd', '3rd', '4th', '5th']
      const occ = ordinals[group.recurrence_occurrence - 1] || `${group.recurrence_occurrence}th`
      return `Monthly on the ${occ} ${DAY_PILLS[group.recurrence_weekday]?.label || ''}`
    }
    return 'Monthly'
  }
  const days = resolveVisitGroupDisplayDays(group, services)
    .map((d) => DAY_PILLS[d]?.label)
    .filter(Boolean)
  const dayText = days.length > 0 ? days.join(', ') : 'No days set'
  if (rtype === 'biweekly' || rtype === 'bi_weekly' || rtype === 'bi-weekly') {
    return `Every other week — ${dayText}`
  }
  return dayText
}

function recurrenceMatchesDate(
  date: Date,
  group: Pick<
    ContractVisitGroup,
    | 'recurrence_type'
    | 'recurrence_days'
    | 'recurrence_weekday'
    | 'recurrence_occurrence'
    | 'recurrence_day_of_month'
  >,
  anchorDate: Date
): boolean {
  return groupRecurrenceMatchesDate(
    group.recurrence_type || 'weekly',
    group.recurrence_days || [],
    date,
    anchorDate,
    {
      weekday: group.recurrence_weekday,
      occurrence: group.recurrence_occurrence,
      dayOfMonth: group.recurrence_day_of_month,
    }
  )
}

export function VisitGroupEditor({
  visitGroups,
  onChange,
  contractServices,
  contractStartDate,
  contractEndDate,
  contractId,
  showAddButton = true,
  showSaveButton = false,
  showPreview = true,
  onSave,
  saving = false,
  onDelete,
  templates,
  onSaveAsTemplate,
  emptyMessage = 'No visit groups yet. Add one to configure recurring generation.',
  title = 'Visit Group Editor',
  subtitle = 'Define recurring visit groups and assign services to each group.',
  scheduleDriven = false,
  crewOptions,
  employeeOptions,
}: VisitGroupEditorProps) {
  const [crews, setCrews] = useState<CrewOption[]>(crewOptions || [])
  const [employees, setEmployees] = useState<EmployeeOption[]>(employeeOptions || [])
  const [assignmentLoadError, setAssignmentLoadError] = useState<string | null>(null)
  const [showServicesSection, setShowServicesSection] = useState(false)
  const [showChecklistsSection, setShowChecklistsSection] = useState(false)
  const [showPreviewSection, setShowPreviewSection] = useState(false)

  useEffect(() => {
    if (crewOptions && employeeOptions) {
      setCrews(crewOptions)
      setEmployees(employeeOptions)
      setAssignmentLoadError(
        crewOptions.length === 0 && employeeOptions.length === 0 ? 'empty' : null
      )
      return
    }
    let cancelled = false
    fetchCrewsAndEmployees().then(({ crews: c, employees: e, loadError }) => {
      if (cancelled) return
      setCrews(c)
      setEmployees(e)
      if (loadError) {
        setAssignmentLoadError(loadError)
      } else if (c.length === 0 && e.length === 0) {
        setAssignmentLoadError('empty')
      } else {
        setAssignmentLoadError(null)
      }
    })
    return () => {
      cancelled = true
    }
  }, [crewOptions, employeeOptions])
  const [expanded, setExpanded] = React.useState<Set<string>>(
    () => new Set(visitGroups.map((g) => g.id))
  )

  const inputCls = 'w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs'
  const { templates: checklistTemplates } = useChecklistTemplates()

  // Remember an assignment when a service is unchecked so re-checking it restores
  // its piggyback schedule instead of silently downgrading it to a primary member.
  const removedAssignmentsRef = React.useRef<Map<string, VisitGroupServiceAssignment>>(new Map())

  // Keep newly-added groups expanded by default
  React.useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev)
      for (const g of visitGroups) {
        if (g.id.startsWith('temp-') && !next.has(g.id)) next.add(g.id)
      }
      return next
    })
  }, [visitGroups])

  const updateField = (groupId: string, field: keyof ContractVisitGroup, value: any) => {
    onChange(
      visitGroups.map((g) => (g.id === groupId ? { ...g, [field]: value } : g))
    )
  }

  const setChecklistOverride = (
    groupId: string,
    serviceItemId: string,
    items: Array<{ label: string; sort_order: number; photo_required?: boolean }> | null
  ) => {
    onChange(
      visitGroups.map((g) => {
        if (g.id !== groupId) return g
        const next = { ...(g.checklist_overrides || {}) }
        if (items === null) {
          delete next[serviceItemId]
        } else {
          next[serviceItemId] = items
        }
        return { ...g, checklist_overrides: next }
      })
    )
  }

  const catalogServicesForGroup = (group: ContractVisitGroup) => {
    const assigned = getGroupAssignments(group)
    const seen = new Set<string>()
    const out: Array<{
      serviceItemId: string
      name: string
      catalogChecklist: Array<{ label: string; sort_order: number; photo_required?: boolean }>
    }> = []
    for (const a of assigned) {
      const svc = contractServices.find((s) => s.id === a.service_id)
      const siId = svc?.service_item_id
      if (!siId || seen.has(siId)) continue
      seen.add(siId)
      out.push({
        serviceItemId: siId,
        name: svc?.service_name || svc?.name || 'Service',
        catalogChecklist: Array.isArray(svc?.default_checklist) ? svc!.default_checklist! : [],
      })
    }
    return out
  }

  const toggleDay = (groupId: string, day: number) => {
    onChange(
      visitGroups.map((g) => {
        if (g.id !== groupId) return g
        const hasDay = (g.recurrence_days || []).includes(day)
        return {
          ...g,
          recurrence_days: hasDay
            ? g.recurrence_days.filter((d) => d !== day)
            : [...(g.recurrence_days || []), day].sort((a, b) => a - b),
        }
      })
    )
  }

  const toggleService = (groupId: string, serviceId: string) => {
    const memoKey = `${groupId}:${serviceId}`
    onChange(
      visitGroups.map((g) => {
        if (g.id !== groupId) return g
        const assignments = getGroupAssignments(g)
        const existing = assignments.find((a) => a.service_id === serviceId)
        if (existing) {
          // Uncheck: remember the assignment (including any piggyback schedule).
          removedAssignmentsRef.current.set(memoKey, existing)
          return syncGroupServiceIds({
            ...g,
            service_assignments: assignments.filter((a) => a.service_id !== serviceId),
          })
        }
        // Re-check: restore the remembered assignment if we have one, else add as primary.
        const restored = removedAssignmentsRef.current.get(memoKey)
        const nextAssignment: VisitGroupServiceAssignment = restored
          ? { ...restored }
          : { service_id: serviceId, schedule_mode: 'primary' }
        return syncGroupServiceIds({
          ...g,
          service_assignments: [...assignments, nextAssignment],
        })
      })
    )
  }

  const updateAssignment = (
    groupId: string,
    serviceId: string,
    patch: Partial<VisitGroupServiceAssignment>
  ) => {
    onChange(
      visitGroups.map((g) => {
        if (g.id !== groupId) return g
        const assignments = getGroupAssignments(g).map((a) =>
          a.service_id === serviceId ? { ...a, ...patch } : a
        )
        return syncGroupServiceIds({ ...g, service_assignments: assignments })
      })
    )
  }

  // Switch a service between "primary" (runs on every group visit) and "piggyback"
  // (its own monthly schedule that only fires on matching group visit dates).
  const setServiceScheduleMode = (
    groupId: string,
    serviceId: string,
    mode: 'primary' | 'piggyback'
  ) => {
    if (mode === 'primary') {
      updateAssignment(groupId, serviceId, {
        schedule_mode: 'primary',
        recurrence_type: null,
        recurrence_days: undefined,
        recurrence_weekday: null,
        recurrence_occurrence: null,
        recurrence_day_of_month: null,
        recurrence_nearest_visit: null,
      })
      return
    }
    const group = visitGroups.find((g) => g.id === groupId)
    const defaultWeekday = group?.recurrence_days?.[0] ?? 1
    updateAssignment(groupId, serviceId, {
      schedule_mode: 'piggyback',
      recurrence_type: 'monthly',
      recurrence_weekday: defaultWeekday,
      recurrence_occurrence: 1,
      recurrence_day_of_month: null,
      // Piggybacks ride the group's visits, so snap to the nearest visit day by default
      recurrence_nearest_visit: true,
    })
  }

  const setPiggybackMonthlyMode = (
    groupId: string,
    serviceId: string,
    monthlyMode: 'nth_weekday' | 'day_of_month'
  ) => {
    if (monthlyMode === 'nth_weekday') {
      const group = visitGroups.find((g) => g.id === groupId)
      const defaultWeekday = group?.recurrence_days?.[0] ?? 1
      updateAssignment(groupId, serviceId, {
        schedule_mode: 'piggyback',
        recurrence_type: 'monthly',
        recurrence_weekday: defaultWeekday,
        recurrence_occurrence: 1,
        recurrence_day_of_month: null,
        recurrence_nearest_visit: true,
      })
      return
    }
    updateAssignment(groupId, serviceId, {
      schedule_mode: 'piggyback',
      recurrence_type: 'monthly',
      recurrence_weekday: null,
      recurrence_occurrence: null,
      recurrence_day_of_month: 15,
      recurrence_nearest_visit: true,
    })
  }

  const toggleDsnyDay = (groupId: string, dayName: string) => {
    onChange(
      visitGroups.map((g) => {
        if (g.id !== groupId) return g
        const days = g.dsny_pickup_days || []
        const has = days.includes(dayName)
        return {
          ...g,
          dsny_pickup_days: has ? days.filter((d) => d !== dayName) : [...days, dayName],
        }
      })
    )
  }

  const addGroup = () => {
    const tempId = `temp-${Date.now()}-${Math.floor(Math.random() * 1000)}`
    onChange([
      ...visitGroups,
      {
        id: tempId,
        contract_id: contractId || '',
        name: 'New Visit Group',
        recurrence_type: 'weekly',
        recurrence_days: [],
        start_date: contractStartDate || null,
        end_date: contractEndDate || null,
        scheduled_start_time: null,
        scheduled_end_time: null,
        billing_driver: null,
        dsny_integration: false,
        dsny_pickup_days: [],
        is_active: true,
        service_ids: [],
        checklist_items: [],
        checklist_overrides: {},
        assigned_crew_id: null,
        assigned_employee_id: null,
      },
    ])
  }

  const removeGroup = async (groupId: string) => {
    if (onDelete) {
      await onDelete(groupId)
      return
    }
    if (!window.confirm('Remove this visit group?')) return
    onChange(visitGroups.filter((g) => g.id !== groupId))
  }

  const applyTemplate = (groupId: string, templateId: string) => {
    const tpl = (templates || []).find((t) => t.id === templateId)
    if (!tpl) return
    onChange(
      visitGroups.map((g) =>
        g.id === groupId
          ? {
              ...g,
              name: tpl.name || g.name,
              recurrence_type: tpl.recurrence_type || g.recurrence_type || 'weekly',
              recurrence_days: tpl.recurrence_days || [],
              scheduled_start_time: tpl.scheduled_start_time ?? g.scheduled_start_time ?? null,
              scheduled_end_time: tpl.scheduled_end_time ?? g.scheduled_end_time ?? null,
              dsny_integration: tpl.dsny_integration ?? g.dsny_integration ?? false,
              dsny_pickup_days: tpl.dsny_pickup_days ?? g.dsny_pickup_days ?? [],
            }
          : g
      )
    )
  }

  const toggleExpanded = (groupId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }

  // Compute next 4 weeks of projected dates per group
  const projectedDates = useMemo(() => {
    const result: Record<string, string[]> = {}
    if (!showPreview) return result
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const horizon = new Date(today)
    horizon.setDate(today.getDate() + 28)
    for (const g of visitGroups) {
      if (g.is_active === false) {
        result[g.id] = []
        continue
      }
      const anchor = g.start_date
        ? new Date(g.start_date + 'T00:00:00')
        : contractStartDate
        ? new Date(contractStartDate + 'T00:00:00')
        : today
      const dates: string[] = []
      const cursor = new Date(today)
      while (cursor <= horizon) {
        if (recurrenceMatchesDate(cursor, g, anchor)) {
          dates.push(cursor.toISOString().split('T')[0])
        }
        cursor.setDate(cursor.getDate() + 1)
      }
      result[g.id] = dates
    }
    return result
  }, [visitGroups, contractStartDate, showPreview])

  const formatDate = (iso: string) => {
    const d = new Date(iso + 'T00:00:00')
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
        </div>
        <div className="flex gap-2">
          {showAddButton && (
            <button
              type="button"
              onClick={addGroup}
              className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <PlusIcon className="w-4 h-4" />
              Add Visit Group
            </button>
          )}
          {showSaveButton && onSave && (
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Visit Groups'}
            </button>
          )}
        </div>
      </div>

      {visitGroups.length === 0 ? (
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      ) : (
        <div className="space-y-4">
          {visitGroups.map((group) => {
            const isExpanded = expanded.has(group.id)
            const projected = projectedDates[group.id] || []
            return (
              <div key={group.id} className="border border-gray-200 rounded-lg">
                {/* Collapsible header */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-t-lg">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(group.id)}
                    className="flex items-center gap-2 flex-1 text-left"
                  >
                    {isExpanded ? (
                      <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="font-medium text-sm text-gray-900">{group.name || 'Unnamed Group'}</span>
                    <span className="text-xs text-gray-500">
                      {group.recurrence_type || 'weekly'}
                      {group.recurrence_days?.length
                        ? ` · ${group.recurrence_days.map((d) => DAY_PILLS[d]?.label).join(', ')}`
                        : ' · no days set'}
                    </span>
                    {(group.assigned_crew_id || group.assigned_employee_id) && (
                      <span className="ml-2 inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                        {group.assigned_crew_id
                          ? crews.find((c) => c.id === group.assigned_crew_id)?.name || 'Crew'
                          : ''}
                        {group.assigned_crew_id && group.assigned_employee_id ? ' · ' : ''}
                        {group.assigned_employee_id
                          ? (() => {
                              const emp = employees.find((e) => e.id === group.assigned_employee_id)
                              return emp ? formatEmployeeLabel(emp) : 'Employee'
                            })()
                          : ''}
                      </span>
                    )}
                    {group.is_active === false && (
                      <span className="ml-2 inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-200 text-gray-600">
                        inactive
                      </span>
                    )}
                  </button>
                  <div className="flex items-center gap-2">
                    {templates && templates.length > 0 && (
                      <select
                        defaultValue=""
                        onChange={(e) => {
                          if (e.target.value) applyTemplate(group.id, e.target.value)
                          e.target.value = ''
                        }}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white"
                        title="Apply a template"
                      >
                        <option value="">Apply template...</option>
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    )}
                    {onSaveAsTemplate && (
                      <button
                        type="button"
                        onClick={() => onSaveAsTemplate(group)}
                        className="px-2 py-1 text-xs text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50"
                        title="Save as reusable template"
                      >
                        Save as Template
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeGroup(group.id)}
                      className="p-1.5 text-red-600 border border-red-200 rounded-md hover:bg-red-50"
                      title="Delete visit group"
                    >
                      <Trash2Icon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="p-3 space-y-2.5">
                    {/* Basics + assignment (always visible, dense) */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="col-span-2">
                        <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Name</label>
                        <input
                          type="text"
                          value={group.name}
                          onChange={(e) => updateField(group.id, 'name', e.target.value)}
                          className={inputCls}
                          placeholder="Visit group name"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                          Recurrence
                        </label>
                        <select
                          value={group.recurrence_type || 'weekly'}
                          onChange={(e) => updateField(group.id, 'recurrence_type', e.target.value)}
                          className={inputCls}
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="bi_weekly">Bi-Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="custom_interval">Custom Interval</option>
                        </select>
                      </div>
                      <div className="flex items-end pb-0.5">
                        <label className="flex items-center gap-1.5 text-[11px] text-gray-700">
                          <input
                            type="checkbox"
                            checked={group.is_active !== false}
                            onChange={(e) => updateField(group.id, 'is_active', e.target.checked)}
                          />
                          Active
                        </label>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                          Crew override
                        </label>
                        <select
                          value={group.assigned_crew_id || ''}
                          onChange={(e) =>
                            updateField(group.id, 'assigned_crew_id', e.target.value || null)
                          }
                          className={inputCls}
                        >
                          <option value="">Inherit from contract / property</option>
                          {crews.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                          Employee override
                        </label>
                        <select
                          value={group.assigned_employee_id || ''}
                          onChange={(e) =>
                            updateField(group.id, 'assigned_employee_id', e.target.value || null)
                          }
                          className={inputCls}
                        >
                          <option value="">Inherit from contract / property</option>
                          {employees.map((e) => (
                            <option key={e.id} value={e.id}>
                              {formatEmployeeLabel(e)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-500">
                      Leave Inherit to use the contract/property default.
                    </p>
                    {assignmentLoadError && (
                      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        {assignmentLoadError === 'empty' ? (
                          <>
                            No crews or employees found.{' '}
                            <Link to="/settings/team" className="underline font-medium">
                              Add them in Team
                            </Link>
                            , then reopen this panel.
                          </>
                        ) : (
                          assignmentLoadError
                        )}
                      </p>
                    )}

                    {/* Dates & times. Date inputs only show for groups containing a seasonal service
                        (Phase 11). They also stay visible if the group already has dates set. */}
                    {(() => {
                      const groupHasSeasonalService = (group.service_ids || []).some((sid) => {
                        const svc = contractServices.find((s: any) => s.id === sid)
                        return Boolean((svc as any)?.is_seasonal)
                      })
                      const showDates =
                        groupHasSeasonalService || !!group.start_date || !!group.end_date
                      return (
                        <div className={`grid grid-cols-2 ${showDates ? 'md:grid-cols-4' : 'md:grid-cols-2'} gap-2`}>
                          {showDates && (
                            <>
                              <div>
                                <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                                  Start Date {groupHasSeasonalService && <span className="text-blue-600">(seasonal)</span>}
                                </label>
                                <input
                                  type="date"
                                  value={group.start_date || ''}
                                  onChange={(e) =>
                                    updateField(group.id, 'start_date', e.target.value || null)
                                  }
                                  className={inputCls}
                                />
                              </div>
                              <div>
                                <label className="block text-[11px] font-medium text-gray-600 mb-0.5">End Date</label>
                                <input
                                  type="date"
                                  value={group.end_date || ''}
                                  onChange={(e) =>
                                    updateField(group.id, 'end_date', e.target.value || null)
                                  }
                                  className={inputCls}
                                />
                              </div>
                            </>
                          )}
                      <div>
                        <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Start Time</label>
                        <input
                          type="time"
                          value={group.scheduled_start_time || ''}
                          onChange={(e) =>
                            updateField(
                              group.id,
                              'scheduled_start_time',
                              e.target.value || null
                            )
                          }
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-gray-600 mb-0.5">End Time</label>
                        <input
                          type="time"
                          value={group.scheduled_end_time || ''}
                          onChange={(e) =>
                            updateField(
                              group.id,
                              'scheduled_end_time',
                              e.target.value || null
                            )
                          }
                          className={inputCls}
                        />
                      </div>
                        </div>
                      )
                    })()}

                    {/* Recurrence days */}
                    <div>
                      <label className="block text-[11px] font-medium text-gray-600 mb-1.5">Days</label>
                      <div className="grid grid-cols-7 gap-1.5">
                        {DAY_PILLS.map((day) => {
                          const selected = (group.recurrence_days || []).includes(day.key)
                          if (scheduleDriven) {
                            return (
                              <span
                                key={day.key}
                                className={`min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-xl border ${
                                  selected
                                    ? 'bg-blue-100 text-blue-700 border-blue-300'
                                    : 'bg-gray-50 text-gray-400 border-gray-200'
                                }`}
                              >
                                {day.label}
                              </span>
                            )
                          }
                          return (
                            <button
                              key={day.key}
                              type="button"
                              onClick={() => toggleDay(group.id, day.key)}
                              className={`min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-xl border transition-colors active:scale-[0.98] ${
                                selected
                                  ? 'bg-blue-100 text-blue-700 border-blue-300'
                                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {day.label}
                            </button>
                          )
                        })}
                      </div>
                      {scheduleDriven && (
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Days come from the Services &amp; Schedule section. Edit them there.
                        </p>
                      )}
                    </div>

                    {/* DSNY block */}
                    <div className="border border-gray-100 rounded-md px-2 py-1.5 bg-gray-50/50">
                      <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!group.dsny_integration}
                          disabled={scheduleDriven}
                          onChange={(e) =>
                            updateField(group.id, 'dsny_integration', e.target.checked)
                          }
                        />
                        DSNY Integration
                      </label>
                      {group.dsny_integration && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-gray-600 mb-1.5">DSNY Pickup Days</p>
                          <div className="grid grid-cols-7 gap-1.5">
                            {DSNY_DAY_OPTIONS.map((dn) => {
                              const selected = (group.dsny_pickup_days || []).includes(dn)
                              if (scheduleDriven) {
                                return (
                                  <span
                                    key={dn}
                                    className={`min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-xl border ${
                                      selected
                                        ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                                        : 'bg-gray-50 text-gray-400 border-gray-200'
                                    }`}
                                  >
                                    {dn.slice(0, 3)}
                                  </span>
                                )
                              }
                              return (
                                <button
                                  key={dn}
                                  type="button"
                                  onClick={() => toggleDsnyDay(group.id, dn)}
                                  className={`min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-xl border transition-colors active:scale-[0.98] ${
                                    selected
                                      ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {dn.slice(0, 3)}
                                </button>
                              )
                            })}
                          </div>
                          {scheduleDriven && (
                            <p className="text-xs text-gray-500 mt-1">
                              Pickup days come from the DSNY schedule. Edit them there.
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Services (collapsed by default) */}
                    <div className="border border-gray-100 rounded-md">
                      <button
                        type="button"
                        onClick={() => setShowServicesSection((v) => !v)}
                        className="w-full flex items-center justify-between px-2 py-1.5 text-left text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        <span>
                          Services ({getGroupAssignments(group).length})
                        </span>
                        {showServicesSection ? (
                          <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
                        ) : (
                          <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </button>
                      {showServicesSection && (
                      <div className="px-2 pb-2 space-y-2 border-t border-gray-100">
                      {contractServices.length === 0 ? (
                        <p className="text-xs text-gray-500 pt-2">No contract services available.</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2">
                          {contractServices.map((service) => {
                            const assignment = getGroupAssignments(group).find(
                              (a) => a.service_id === service.id
                            )
                            const checked = !!assignment
                            const isPiggyback = assignment?.schedule_mode === 'piggyback'
                            const monthlyMode =
                              assignment?.recurrence_day_of_month != null
                                ? 'day_of_month'
                                : 'nth_weekday'
                            return (
                              <div
                                key={service.id}
                                className={`rounded-md border px-2 py-1.5 ${
                                  isPiggyback
                                    ? 'border-amber-200 bg-amber-50/40'
                                    : 'border-gray-100'
                                }`}
                              >
                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleService(group.id, service.id)}
                                  />
                                  <span className="flex-1">
                                    {service.service_name || service.name || 'Service'}
                                  </span>
                                  {isPiggyback && (
                                    <span className="inline-flex px-1.5 py-0.5 text-[10px] rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                                      piggyback
                                    </span>
                                  )}
                                </label>

                                {checked && (
                                  <div className="mt-2 pl-6 space-y-2">
                                    <select
                                      value={isPiggyback ? 'piggyback' : 'primary'}
                                      onChange={(e) =>
                                        setServiceScheduleMode(
                                          group.id,
                                          service.id,
                                          e.target.value as 'primary' | 'piggyback'
                                        )
                                      }
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                                    >
                                      <option value="primary">Runs on every group visit</option>
                                      <option value="piggyback">
                                        Piggyback — its own monthly schedule
                                      </option>
                                    </select>

                                    {isPiggyback && (
                                      <div className="space-y-2">
                                        <div className="flex gap-3">
                                          <label className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer">
                                            <input
                                              type="radio"
                                              name={`pm-${group.id}-${service.id}`}
                                              checked={monthlyMode === 'nth_weekday'}
                                              onChange={() =>
                                                setPiggybackMonthlyMode(
                                                  group.id,
                                                  service.id,
                                                  'nth_weekday'
                                                )
                                              }
                                            />
                                            Nth weekday
                                          </label>
                                          <label className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer">
                                            <input
                                              type="radio"
                                              name={`pm-${group.id}-${service.id}`}
                                              checked={monthlyMode === 'day_of_month'}
                                              onChange={() =>
                                                setPiggybackMonthlyMode(
                                                  group.id,
                                                  service.id,
                                                  'day_of_month'
                                                )
                                              }
                                            />
                                            Day of month
                                          </label>
                                        </div>

                                        {monthlyMode === 'nth_weekday' ? (
                                          <div className="grid grid-cols-2 gap-2">
                                            <select
                                              value={assignment?.recurrence_occurrence ?? 1}
                                              onChange={(e) =>
                                                updateAssignment(group.id, service.id, {
                                                  recurrence_occurrence: parseInt(
                                                    e.target.value,
                                                    10
                                                  ),
                                                })
                                              }
                                              className="px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                                            >
                                              <option value={1}>1st</option>
                                              <option value={2}>2nd</option>
                                              <option value={3}>3rd</option>
                                              <option value={4}>4th</option>
                                              <option value={5}>Last</option>
                                            </select>
                                            <select
                                              value={assignment?.recurrence_weekday ?? 1}
                                              onChange={(e) =>
                                                updateAssignment(group.id, service.id, {
                                                  recurrence_weekday: parseInt(e.target.value, 10),
                                                })
                                              }
                                              className="px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                                            >
                                              {DAY_PILLS.map((d) => (
                                                <option key={d.key} value={d.key}>
                                                  {d.label}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-gray-600">
                                              Day of month
                                            </span>
                                            <input
                                              type="number"
                                              min={1}
                                              max={31}
                                              value={assignment?.recurrence_day_of_month ?? 15}
                                              onChange={(e) =>
                                                updateAssignment(group.id, service.id, {
                                                  recurrence_day_of_month: Math.min(
                                                    31,
                                                    Math.max(
                                                      1,
                                                      parseInt(e.target.value, 10) || 1
                                                    )
                                                  ),
                                                })
                                              }
                                              className="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                                            />
                                          </div>
                                        )}
                                        <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={
                                              assignment?.recurrence_nearest_visit ?? false
                                            }
                                            onChange={(e) =>
                                              updateAssignment(group.id, service.id, {
                                                recurrence_nearest_visit: e.target.checked,
                                              })
                                            }
                                          />
                                          Run on the group visit day closest to that date
                                        </label>
                                        <p className="text-[11px] text-gray-500">
                                          Added only on matching group visits — the visit schedule
                                          itself still follows the group.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                      </div>
                      )}
                    </div>

                    {/* Checklists (collapsed by default) */}
                    <div className="border border-gray-100 rounded-md">
                      <button
                        type="button"
                        onClick={() => setShowChecklistsSection((v) => !v)}
                        className="w-full flex items-center justify-between px-2 py-1.5 text-left text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        <span>Checklists &amp; catalogue</span>
                        {showChecklistsSection ? (
                          <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
                        ) : (
                          <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </button>
                      {showChecklistsSection && (
                      <div className="px-2 pb-2 space-y-3 border-t border-gray-100 pt-2">
                    {/* Item catalogue work/checklist preview */}
                    {(() => {
                      const assigned = getGroupAssignments(group)
                        .map((a) => contractServices.find((s) => s.id === a.service_id))
                        .filter((s): s is ContractServiceLite => !!s)
                      const withContent = assigned.filter(
                        (s) =>
                          (s.work_description && s.work_description.trim()) ||
                          (s.default_checklist && s.default_checklist.length > 0)
                      )
                      if (withContent.length === 0) return null
                      return (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-800">
                            From item catalogue
                          </p>
                          <p className="text-xs text-gray-500">
                            Comes from the item catalogue — edit there. Seeded automatically when
                            jobs are generated for days each item fires.
                          </p>
                          {withContent.map((s) => (
                            <div
                              key={s.id}
                              className="rounded-lg border border-gray-200 bg-gray-50 p-2 text-sm"
                            >
                              <p className="font-medium text-gray-900">
                                {s.service_name || s.name || 'Service'}
                              </p>
                              {s.work_description?.trim() && (
                                <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">
                                  {s.work_description}
                                </p>
                              )}
                              {(s.default_checklist || []).length > 0 && (
                                <ul className="mt-1 text-xs text-gray-600 list-disc list-inside">
                                  {(s.default_checklist || []).map((t, i) => (
                                    <li key={i}>{t.label}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      )
                    })()}

                    {/* Catalogue checklist overrides (this contract / visit group) */}
                    {(() => {
                      const catalogSvcs = catalogServicesForGroup(group)
                      if (catalogSvcs.length === 0) return null
                      const overrides = group.checklist_overrides || {}
                      return (
                        <div className="space-y-2">
                          <div>
                            <p className="text-xs font-medium text-gray-800">
                              Catalogue checklists (this contract)
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Customize an item&apos;s checklist for this visit group only. Until
                              customized, jobs use the Service Catalog default.
                            </p>
                          </div>
                          {catalogSvcs.map((cs) => {
                            const isOverridden = Object.prototype.hasOwnProperty.call(
                              overrides,
                              cs.serviceItemId
                            )
                            const rows = isOverridden
                              ? overrides[cs.serviceItemId] || []
                              : cs.catalogChecklist
                            return (
                              <div
                                key={cs.serviceItemId}
                                className="rounded-lg border border-gray-200 bg-gray-50/60 p-2 space-y-2"
                              >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <p className="text-sm font-medium text-gray-900">{cs.name}</p>
                                  <div className="flex gap-2">
                                    {!isOverridden ? (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setChecklistOverride(
                                            group.id,
                                            cs.serviceItemId,
                                            (() => {
                                              const copied = normalizeChecklistItems(cs.catalogChecklist)
                                              return copied.length
                                                ? copied
                                                : [{ label: '', sort_order: 0, photo_required: false }]
                                            })()
                                          )
                                        }
                                        className="text-xs font-medium text-blue-700 hover:text-blue-900"
                                      >
                                        Customize
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setChecklistOverride(group.id, cs.serviceItemId, null)
                                        }
                                        className="text-xs font-medium text-gray-600 hover:text-gray-900"
                                      >
                                        Reset to catalog
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {!isOverridden && rows.length === 0 && (
                                  <p className="text-xs text-gray-400">No catalog checklist tasks.</p>
                                )}
                                {!isOverridden &&
                                  rows.map((item, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center justify-between gap-2 text-xs text-gray-700 px-1"
                                    >
                                      <span>{item.label}</span>
                                      {item.photo_required && (
                                        <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                                          Photo req.
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                {isOverridden && (
                                  <>
                                    {rows.map((item, idx) => (
                                      <div key={idx} className="flex flex-wrap items-center gap-2">
                                        <input
                                          type="text"
                                          value={item.label}
                                          onChange={(e) => {
                                            const next = [...rows]
                                            next[idx] = { ...next[idx], label: e.target.value }
                                            setChecklistOverride(group.id, cs.serviceItemId, next)
                                          }}
                                          className="flex-1 min-w-[10rem] px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white"
                                          placeholder="Checklist item…"
                                        />
                                        <label className="inline-flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                                          <input
                                            type="checkbox"
                                            checked={!!item.photo_required}
                                            onChange={(e) => {
                                              const next = [...rows]
                                              next[idx] = {
                                                ...next[idx],
                                                photo_required: e.target.checked,
                                              }
                                              setChecklistOverride(group.id, cs.serviceItemId, next)
                                            }}
                                            className="rounded border-gray-300"
                                          />
                                          Photo req.
                                        </label>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const next = rows.filter((_, i) => i !== idx)
                                            setChecklistOverride(
                                              group.id,
                                              cs.serviceItemId,
                                              next.map((r, i) => ({ ...r, sort_order: i }))
                                            )
                                          }}
                                          className="text-red-500"
                                        >
                                          <Trash2Icon className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setChecklistOverride(group.id, cs.serviceItemId, [
                                          ...rows,
                                          {
                                            label: '',
                                            sort_order: rows.length,
                                            photo_required: false,
                                          },
                                        ])
                                      }
                                      className="text-xs text-blue-600 hover:underline"
                                    >
                                      + Add task
                                    </button>
                                  </>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )
                    })()}

                    {/* Additional group checklist (extras beyond catalogue items) */}
                    <div className="border-t border-gray-100 pt-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-gray-800">
                          Additional group checklist
                        </p>
                        {checklistTemplates.length > 0 && (
                          <select
                            defaultValue=""
                            onChange={(e) => {
                              const tpl = checklistTemplates.find((t) => t.id === e.target.value)
                              e.target.value = ''
                              if (!tpl) return
                              updateField(
                                group.id,
                                'checklist_items',
                                normalizeChecklistItems(tpl.items)
                              )
                            }}
                            className="text-xs border border-gray-300 rounded-lg px-2 py-1"
                          >
                            <option value="">Apply template…</option>
                            {sortTemplatesForServiceType(checklistTemplates).map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        Group-wide extras (e.g. &quot;lock the gate&quot;) seeded after each item&apos;s
                        checklist on new jobs from this visit group.
                      </p>
                      {(group.checklist_items || []).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={item.label}
                            onChange={(e) => {
                              const next = [...(group.checklist_items || [])]
                              next[idx] = { ...next[idx], label: e.target.value }
                              // Do not normalize/trim while typing — trim drops trailing spaces
                              updateField(group.id, 'checklist_items', next)
                            }}
                            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                            placeholder="Checklist item…"
                          />
                          <label className="inline-flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={!!item.photo_required}
                              onChange={(e) => {
                                const next = [...(group.checklist_items || [])]
                                next[idx] = {
                                  ...next[idx],
                                  photo_required: e.target.checked,
                                }
                                updateField(group.id, 'checklist_items', next)
                              }}
                              className="rounded border-gray-300"
                            />
                            Photo req.
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const next = (group.checklist_items || []).filter((_, i) => i !== idx)
                              updateField(group.id, 'checklist_items', normalizeChecklistItems(next))
                            }}
                            className="text-red-500"
                          >
                            <Trash2Icon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const next = [
                            ...(group.checklist_items || []),
                            {
                              label: '',
                              sort_order: (group.checklist_items || []).length,
                              photo_required: false,
                            },
                          ]
                          updateField(group.id, 'checklist_items', next)
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <PlusIcon className="w-3.5 h-3.5" />
                        Add checklist item
                      </button>
                    </div>
                      </div>
                      )}
                    </div>

                    {/* Preview (collapsed by default) */}
                    {showPreview && (
                      <div className="border border-gray-100 rounded-md">
                        <button
                          type="button"
                          onClick={() => setShowPreviewSection((v) => !v)}
                          className="w-full flex items-center justify-between px-2 py-1.5 text-left text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <CalendarDaysIcon className="w-3.5 h-3.5 text-gray-400" />
                            Preview ({projected.length})
                          </span>
                          {showPreviewSection ? (
                            <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
                          ) : (
                            <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />
                          )}
                        </button>
                        {showPreviewSection && (
                          <div className="px-2 pb-2 border-t border-gray-100 pt-2">
                            {projected.length === 0 ? (
                              <p className="text-[11px] text-gray-500 italic">
                                No visits projected. Set recurrence days to see a preview.
                              </p>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {projected.map((d) => (
                                  <span
                                    key={d}
                                    className="inline-flex px-2 py-0.5 text-[11px] rounded bg-blue-50 text-blue-700 border border-blue-100"
                                  >
                                    {formatDate(d)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Validate a list of visit groups for activation. Returns an array of error strings.
 * Empty array means the groups are good to activate.
 */
export function validateVisitGroups(groups: ContractVisitGroup[]): string[] {
  const errors: string[] = []
  const active = groups.filter((g) => g.is_active !== false)
  if (active.length === 0) {
    errors.push('At least one active visit group is required.')
    return errors
  }
  for (const g of active) {
    if (!g.name || !g.name.trim()) {
      errors.push(`Visit group is missing a name.`)
    }

    const assignments: VisitGroupServiceAssignment[] =
      g.service_assignments && g.service_assignments.length > 0
        ? g.service_assignments
        : (g.service_ids || []).map((id) => ({
            service_id: id,
            schedule_mode: 'primary' as const,
          }))

    const primaryAssignments = assignments.filter((a) => a.schedule_mode !== 'piggyback')

    if (assignments.length === 0) {
      errors.push(`Visit group "${g.name}" has no services assigned.`)
    }

    if (
      primaryAssignments.length > 0 &&
      (!g.recurrence_days || g.recurrence_days.length === 0) &&
      g.recurrence_type !== 'daily' &&
      g.recurrence_type !== 'monthly'
    ) {
      errors.push(`Visit group "${g.name}" has no recurrence days selected.`)
    }

    if (primaryAssignments.length === 0 && assignments.length > 0) {
      errors.push(
        `Visit group "${g.name}" has only piggyback services — add at least one primary service to drive the visit schedule.`
      )
    }
  }
  return errors
}

/**
 * Auto-suggest visit groups from a list of contract services.
 * Groups services by their recurrence_days set; services with the same days merge into one group.
 */
type ServiceForSuggestion = {
  id: string
  service_name?: string
  name?: string
  recurrence_days?: number[]
}

export function suggestVisitGroupsFromServices(
  services: ServiceForSuggestion[],
  contractId: string,
  contractStartDate?: string | null,
  contractEndDate?: string | null
): ContractVisitGroup[] {
  if (!services || services.length === 0) return []

  const buckets: Record<string, { days: number[]; services: ServiceForSuggestion[] }> = {}
  for (const s of services) {
    const days = (s.recurrence_days || []).slice().sort((a: number, b: number) => a - b)
    const key = days.join(',') || 'none'
    if (!buckets[key]) buckets[key] = { days, services: [] }
    buckets[key].services.push(s)
  }

  const out: ContractVisitGroup[] = []
  let idx = 0
  Object.values(buckets).forEach((bucket) => {
    idx++
    const dayLabel =
      bucket.days.length > 0
        ? bucket.days
            .map((d: number) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d])
            .join('/')
        : 'Unscheduled'
    const serviceLabel = bucket.services
      .map((s: ServiceForSuggestion) => s.service_name || s.name || 'Service')
      .join(' + ')
    out.push({
      id: `temp-suggest-${Date.now()}-${idx}`,
      contract_id: contractId,
      name:
        bucket.services.length === 1
          ? `${serviceLabel} (${dayLabel})`
          : `${dayLabel} Visit`,
      recurrence_type: 'weekly',
      recurrence_days: bucket.days,
      start_date: contractStartDate || null,
      end_date: contractEndDate || null,
      scheduled_start_time: null,
      scheduled_end_time: null,
      billing_driver: null,
      dsny_integration: false,
      dsny_pickup_days: [],
      is_active: true,
      service_ids: bucket.services.map((s: ServiceForSuggestion) => s.id),
    })
  })
  return out
}
